/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {getDefaultYearRange, range} from '../../../core/helpers/generic-helpers';
import {LoadingCounter} from '../../../core/helpers/loading-counter';
import {StateService} from '../../../core/state/state.service';
import {SelectableOptions} from '../../../models/selectable-options';
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {BrandfamService} from '../../../services/brandfam.service';
import {ExcelExportService} from '../../../services/excel-export.service';
import {MarketingDivisionService} from '../../../services/marketing-division.service';
import {ProgramCodeService} from '../../../services/program-code.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';
import {RegionService} from '../../../services/region.service';


class DuplicateError extends Error {
  constructor(m) {
    super(m);
  }
}

@Component({
    selector: 'app-create-propulsion-forecast-template',
    templateUrl: './create-propulsion-forecast-template.component.html',
    styleUrl: './create-propulsion-forecast-template.component.scss',
    standalone: false
})
export class CreatePropulsionForecastTemplateComponent implements OnInit {

  SUBREGION = 'SUBREGION';
  MKTAREA = 'MKTAREA';
  PLNGMKT = 'PLNGMKT';
  selectedGeography: number;
  detailData: any[] = [];
  filteredData: any[] = [];


  regions: any[];
  selectedRegion: string;
  geographyFilters: SelectableOptions = new SelectableOptions();

  planningMarkets = new SelectableOptions();
  marketDivisions = new SelectableOptions();
  brandfams = new SelectableOptions();
  programCodes = new SelectableOptions();
  createMultipleWorkbooks: boolean;
  startYear: number;
  endYear: number;
  yearRange: any;
  selectedVehicleScenario: VehicleScenario;
  duplicateRecordData: any[];

  selectedFilter: number;
  readonly loading = new LoadingCounter();

  constructor(public snackBar: FabMessageService,
              readonly propService: PropulsionScenarioService,
              readonly regionService: RegionService,
              readonly marketDivisionService: MarketingDivisionService,
              readonly programCodeService: ProgramCodeService,
              readonly brandFamService: BrandfamService,
              public stateService: StateService,
              public dialog: MatDialog,
              public readonly excelService: ExcelExportService) {
  }

  get showSpinner(): boolean {
    return this.loading.active;
  }

  ngOnInit() {
    this.selectedVehicleScenario = this.stateService.selectedScenario;
    this.getPropScenYearRange(this.selectedVehicleScenario.propulsionScenario.id)
    this.getRegions();
    this.getTemplateFilterData();
    this.selectedFilter = 0;
    this.selectedGeography = 0;
  }

  /**
   * gets the year range for the propulsion scenario
   * @param propScenId
   */
  getPropScenYearRange(propScenId: number): void {
    this.loading.track(this.propService.getYearRange(propScenId)).subscribe({
      next: (data) => {
        const scenarioMinYear = data[0].MIN_YEAR;
        const scenarioMaxYear = data[0].MAX_YEAR;
        const {startYear, endYear} = getDefaultYearRange();
        // If there is no data returned from the scenario, use the default year range
        if (scenarioMinYear == null || scenarioMaxYear == null) {
          this.startYear = startYear;
          this.endYear = endYear;
          this.yearRange = range(this.startYear - 20, this.endYear + 21);
        } else if (startYear < scenarioMaxYear) {
          // Year range is +- 20 of the current year range of the scenario
          // If the default year range is a valid range of volumes in the scenario, then use the default year range.
          // else use the year range values from the scenario
          this.startYear = Math.max(scenarioMinYear, startYear);
          this.endYear = Math.min(endYear, scenarioMaxYear);
          this.yearRange = range(Math.min(scenarioMinYear, startYear) - 20, Math.max(endYear, scenarioMaxYear) + 21);
        } else {
          this.startYear = scenarioMinYear;
          this.endYear = scenarioMaxYear;
          this.yearRange = range(this.startYear - 20, this.endYear + 21);
        }
      },
      error: (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      }
    });
  }

  /**
   * Gets the regions from the propulsion scenario ID
   */
  getRegions() {
    this.loading.track(this.regionService.getAllRegionsByPropulsionScenario(this.stateService.getUserGmid(),
      this.selectedVehicleScenario.propulsionScenario.id)).subscribe({
      next: (data) => {
        this.regions = data;
        this.selectedRegion = this.regions[0].region;
      },
      error: (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.getPlanningMarketsAndFilters();
      }
    });
  }

  /**
   * gets the planning markets
   */
  getPlanningMarketsAndFilters() {
    this.clearAllFilters();
    this.loading.track(this.propService.getFiltersForPropulsionTemplate(this.selectedRegion,
      this.selectedVehicleScenario.propulsionScenario.id)).subscribe({
      next: (data) => {
        this.detailData = data;
        this.filteredData = data;
      },
      error: (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.initializeFilterOptions();
      }
    });
  }

  /**
   * gets market divisions
   */
  getMarketDivisions() {
    this.loading.track(this.marketDivisionService
    .getMarketDivisionsByPropulsionScenario(this.selectedVehicleScenario.propulsionScenario.id)).subscribe({
      next: (data) => {
        this.marketDivisions.options = data;
      },
      error: (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      }
    });
  }

  /**
   * gets the brandfams
   */
  getBrandfams() {
    this.loading.track(this.brandFamService
    .getBrandfamsByPropulsionScenario(this.selectedVehicleScenario.propulsionScenario.id)).subscribe({
      next: (data) => {
        this.brandfams.options = data;
      },
      error: (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      }
    });
  }

  /**
   * gets the program codes
   */
  getProgramCodes() {
    this.loading.track(this.programCodeService
    .getProgramCodesByPropulsionScenario(this.selectedVehicleScenario.propulsionScenario.id)).subscribe({
      next: (data) => {
        this.programCodes.options = data;
      },
      error: (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      }
    });
  }

  /**
   * event handler for the filters for the secondary selections
   */
  onSubFilterChange() {
    this.programCodes.selectedOptions = [];
    this.marketDivisions.selectedOptions = [];
    this.brandfams.selectedOptions = [];
  }

  /**
   * gets the filter data for the chosen vehicle scenario
   */
  getTemplateFilterData() {
    this.getProgramCodes();
    this.getMarketDivisions();
    this.getBrandfams();
  }


  /**
   * Initialize the filter options for all the filters
   */
  initializeFilterOptions(): void {
    if (this.selectedGeography === 0) {
      this.geographyFilters.options = this.setFilterOption(this.SUBREGION);
    } else {
      this.geographyFilters.options = this.setFilterOption(this.MKTAREA);
    }
    this.setCascadingFilterOptions();
  }


  /**
   * Helper function to return back a list of available filter options for the specified filter
   * @param filterFieldName the name of the filter to set the filter options for
   */
  setFilterOption(filterFieldName: string): string[] {
    return [...new Set(this.filteredData
    .sort((a, b) => a[filterFieldName].toUpperCase() > b[filterFieldName].toUpperCase() ? 1 : -1)
    .map(detailRecord => detailRecord[filterFieldName].toUpperCase()))];
  }

  /**
   * Function used to set the filter options for the passed in filter + any of the cascaded filters below it
   */
  setCascadingFilterOptions(): void {
    this.planningMarkets.options = this.setFilterOption(this.PLNGMKT);
    this.planningMarkets.selectedOptions = [];
  }

  /**
   * Event handler whenever a filter changes
   */
  onFilterChange(): void {
    this.programCodes.selectedOptions = [];
    this.marketDivisions.selectedOptions = [];
    this.brandfams.selectedOptions = [];

    // Reset the filtered data to the original data
    this.filteredData = this.detailData;
    // Reset the selected filters for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions();


    // Update the filtered data based off of which filters are selected
    if (this.selectedGeography === 0) {
      this.applyFilter(this.SUBREGION, this.geographyFilters.selectedOptions);
    } else {
      this.applyFilter(this.MKTAREA, this.geographyFilters.selectedOptions);
    }

    // Update the filter options for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions();
  }


  /**
   * Reset all the filter options from the filter dropdowns
   */
  resetAllSelectedFilters(): void {
    this.geographyFilters.selectedOptions = [];
    this.planningMarkets.selectedOptions = [];
  }


  /**
   * Function used to update the filtered data based off of the selected filters for a particular filter type
   * @param filterType the filter type (which filter it is)
   * @param selectedFilters list of filter values that were selected from the filter
   */
  applyFilter(filterType: string, selectedFilters: any): void {
    if (selectedFilters.length > 0) {
      this.filteredData = this.filteredData.filter(detailRecord => selectedFilters.includes(detailRecord[filterType].toUpperCase()));
    }
  }

  /**
   * cleears all the filters to reset them with original data
   */
  clearAllFilters() {
    this.geographyFilters = new SelectableOptions();
    this.planningMarkets = new SelectableOptions();
  }

  /**
   * Master async method to create a propulsion forecast template
   */
  async createForecastTemplate() {
    this.loading.begin();
    try {
      await this.getPropulsionDuplicates();
      await this.setupForecastTemplate();

      const cleanPlanningMarkets = this.planningMarkets.selectedOptions
      .map(element => this.cleanName(element));
      if (this.createMultipleWorkbooks) {
        for (const planningMarket of cleanPlanningMarkets) {
          await this.retrieveTemplateFiles([planningMarket]);
        }
      } else {
        await this.retrieveTemplateFiles(cleanPlanningMarkets);
      }
    } catch (error) {
      this.snackBar.displayError(error.message, DISMISS_MSG);
    } finally {
      this.loading.end();
    }
  }

  /**
   * Get duplicate records of the specified Propulsion Scenario
   */
  async getPropulsionDuplicates() {
    try {
      const duplicatePromise = this.propService.getPropDupeRecords(this.selectedVehicleScenario.propulsionScenario.id).toPromise();
      this.duplicateRecordData = await duplicatePromise;
      if (this.duplicateRecordData.length > 0) {
        throw (new DuplicateError('This scenario contains duplicate records. Contact Central Office Admin'))
      }
    } catch (error) {
      if (error instanceof DuplicateError) {
        throw error;
      }
      throw (new Error("Error checking for duplicates"));
    }
  }

  /**
   * sets up and retrieves forecast templates
   */
  async setupForecastTemplate() {
    const {filterOptions, formattedMarketDivisions, formattedPlanningMarkets} = this.formatParameters();
    try {
      const setupPromise = this.propService.setUpPropulsionTemplate(this.selectedVehicleScenario.propulsionScenario.id,
        this.selectedVehicleScenario.vehScenId, this.selectedRegion, formattedPlanningMarkets, filterOptions, this.startYear,
        this.endYear, this.stateService.getUserGmid(), formattedMarketDivisions).toPromise();
      await setupPromise;
    } catch (error) {
      throw (new Error(error));
    }
  }

  /**
   * After setup, fetches template file from backend
   * @param planningMarkets a list of planning markets to get a template for
   */
  async retrieveTemplateFiles(planningMarkets: string[]) {
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    let fileName = 'PropFT_' + this.selectedVehicleScenario.propulsionScenario.id + "_" + this.selectedVehicleScenario.propulsionScenario.propulsionScenarioName + "_";
    if (planningMarkets.length === 1) {
      fileName += planningMarkets[0] + "_";
    }
    fileName += currDate;
    try {
      const filePromise = this.propService.createPropulsionTemplate(this.stateService.getUserGmid(),
        this.startYear, this.endYear, planningMarkets).toPromise();
      const response = await filePromise;
      this.excelService.exportToExcelFromServer(response, fileName);
    } catch (error) {
      throw (new Error(error));
    }
  }

  /**
   * takes the lists of selected options and formats them to work in the stored procedure
   */
  formatParameters() {
    let filterOptions = '~ALL';
    let formattedMarketDivisions = 'ALL';
    switch (this.selectedFilter) {
      case 0:
        if (this.marketDivisions.selectedOptions.length !== 0) {
          formattedMarketDivisions = '\'' + this.marketDivisions.getSelectedOptionsString('\',\'') + '\'';
        }
        break;
      case 1:
        if (this.brandfams.selectedOptions.length !== 0) {
          filterOptions = '\'' + this.brandfams.getSelectedOptionsString('\',\'') + '\'';
        }
        break;
      case 2:
        if (this.programCodes.selectedOptions.length !== 0) {
          filterOptions = '~\'' + this.programCodes.getSelectedOptionsString('\',\'') + '\'';
        }
        break;
    }
    const formattedPlanningMarkets = '\'' + this.planningMarkets.getSelectedOptionsString('\',\'') + '\'';
    return {filterOptions, formattedMarketDivisions, formattedPlanningMarkets};
  }

  /**
   * removes spaces, dashes, and slashes from a string
   * @param uncleanName the string to format
   * @private
   */
  private cleanName(uncleanName: string): string {
    uncleanName = uncleanName.replace(/ /g, "");
    uncleanName = uncleanName.replace(/-/g, "");
    uncleanName = uncleanName.replace(/\//g, "");
    uncleanName = uncleanName.replace(/\./g, "");
    return uncleanName;
  }
}
